import {type DBSchema, type IDBPDatabase, openDB} from 'idb';

import type {Nullable} from '../../../utils/utility-types';
import type {AddItemOptions, Config, IBlobDBService} from './types';

import {logger} from '../../../utils/logger';
import {FlagManager} from '../../../utils/store';

interface MediaDBSchema extends DBSchema {
  store: {
    key: string;
    value: Blob;
  };
}

export class BlobDBService implements IBlobDBService {
  private db: Nullable<IDBPDatabase<MediaDBSchema>> = null;
  private isEnabledFlag = new FlagManager();
  private readonly TEST_DATABASE_NAME = 'blob-write-test';

  constructor(private readonly config: Config) {
    this.init = this.init.bind(this);
  }

  async init() {
    if (this.db) {
      return;
    }

    const isEnabled = await this.checkIsEnabled();

    if (isEnabled) {
      this.db = await openDB<MediaDBSchema>(this.config.dbName, 1, {
        upgrade(db) {
          db.createObjectStore('store');
        },
      });

      this.checkQuotaAndManageStorage();
    }

    this.isEnabledFlag.setValue(isEnabled);
  }

  destruct() {
    if (this.db) {
      this.db.close();
      this.db = null;
    }
  }

  get isEnabled() {
    return this.isEnabledFlag.value;
  }

  // param _ - options are available only for mobile implementation at the moment
  async add(
    key: string,
    item: Blob | string,
    _?: AddItemOptions,
  ): Promise<string> {
    if (typeof item === 'string') {
      logger.error(
        `Can't save the item: ${item}. Saving file by path is not supported on web, only on mobile. Use Blob instead.`,
      );

      return key;
    }

    if (this.db) {
      await this.db.put('store', item, key);
    }

    return key;
  }

  async clear(): Promise<void> {
    if (this.db) {
      await this.db.clear('store');
    }
  }

  async get(key: string): Promise<Blob | string | undefined> {
    return this.db?.get('store', key);
  }

  async remove(key: string): Promise<void> {
    if (this.db) {
      await this.db.delete('store', key);
    }
  }

  protected log(message: string) {
    // eslint-disable-next-line no-console
    console.warn(`${this.config.loggingPrefix} ${message}`);
  }

  private async blobWriteTest(): Promise<boolean> {
    try {
      const storeName = 'testStore';
      const key = 'testKey';
      const db = await openDB(this.TEST_DATABASE_NAME, 1, {
        upgrade(db) {
          db.createObjectStore(storeName);
        },
      });

      const recordsCount = await db.count(storeName);

      if (recordsCount) {
        return true;
      }

      try {
        const blob = new Blob(['test'], {type: 'text/plain'});

        await db.put(storeName, blob, key);

        return true;
      } catch (e) {
        this.log(`Blob write check failed: ${e.message}`);

        return false;
      } finally {
        db.close();
      }
    } catch (e) {
      this.log(`Blob write check failed (Open DB): ${e.message}`);

      return false;
    }
  }

  private async checkIsEnabled(): Promise<boolean> {
    const isTopLvl = !this.checkIsIframe();
    const hasIndexedDb = 'indexedDB' in window;

    if (!hasIndexedDb || !isTopLvl) {
      return false;
    }

    const isBlobWriteSupported = await this.blobWriteTest();
    const hasStorage = Boolean(navigator.storage);

    return isBlobWriteSupported && hasStorage;
  }

  private checkIsIframe() {
    return window !== window.top;
  }

  /**
   * Checks the quota and usage for the current origin.
   * Clears storage if usage exceeds config.dbUsageMaxPercentage of the quota.
   */
  private async checkQuotaAndManageStorage() {
    if (!navigator.storage?.estimate) {
      this.log('Storage estimation API not supported in this browser.');

      return;
    }

    const {quota, usage} = await navigator.storage.estimate();

    if (quota === undefined || usage === undefined) {
      this.log('Failed to estimate storage quota and usage');

      return;
    }

    const usagePercentage = ((usage / quota) * 100).toFixed(2);

    this.log(
      `Quota: ${quota} bytes, Usage: ${usage} bytes (${usagePercentage}%)`,
    );

    if (!this.config.dbUsageMaxPercentage) {
      return;
    }

    if (usage / quota > this.config.dbUsageMaxPercentage / 100) {
      this.log(
        `Usage exceeds ${this.config.dbUsageMaxPercentage}% of the quota. Clearing storage...`,
      );
      await this.clear();
    }
  }
}
